import * as Sentry from '@sentry/react'
import { SeverityLevel } from '@sentry/types'
import { each, get, set } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useRef } from 'react'
import { Identifier } from 'react-admin'
import { ProductSubscriptionBundleName } from 'shared/models/productSubscription'

import { BrokerStatus } from '../components/Brokers/BrokerStatusTransitions'
import { detectEnvironment, Environment, isProductionEnv } from '../config/config'
import { BundleChoices, CashAdvancePaymentStatuses, ELDTypeName, EquipmentType } from './constants'

export const getFormattedDate = (
  date: string,
  timezone: string = 'America/Los_Angeles',
  includeDate: boolean = true
): any => {
  let dateTime = DateTime.fromISO(date).setZone(timezone)
  if (includeDate) {
    return dateTime.toLocaleString({ ...DateTime.DATETIME_FULL, hourCycle: 'h23' })
  } else {
    return dateTime.toFormat('HH:mm ZZZZ')
  }
}

export const getLoadStop = (record: any, stopType: any) => {
  return record.stops.find((stop: any) => stop.type === stopType)
}

export const getLocalizedStopTime = (load: any, stopType: any, window: any) => {
  const stop = getLoadStop(load, stopType)
  if (!stop) {
    return null
  }
  const dateString = get(stop, window)
  return getFormattedDate(dateString, stop.location_timezone)
}

export const getUserFullName = (driver: any) => {
  if (!driver) {
    return undefined
  }
  if (driver.first_name === '' && driver.last_name === '') {
    return undefined
  }
  return `${driver.first_name} ${driver.last_name}`
}

export const getUsernameFromEmail = (user: any) => {
  return user?.email?.split('@')[0] || ''
}

export const getTeamAndProductName = (team: any, teamName?: string, productSubscriptionName?: string) => {
  const missingTeam = !team

  if (teamName) {
    if (!productSubscriptionName) {
      return `${teamName} (No product subscription)`
    } else {
      return `${teamName} (${productSubscriptionName})`
    }
  }

  if (missingTeam) {
    return null
  } else if (!team.formatted_subscription_name) {
    return `${team.name} (No product subscription)`
  } else {
    return `${team.name} (${team.formatted_subscription_name})`
  }
}

export const getProductName = (bundleName: any, isShortName: boolean = false) => {
  const bundle = getBundleById(bundleName)
  if (bundle) {
    const productName = bundle['name']
    return isShortName ? productName.toUpperCase() : productName
  }
  return 'No product subscription'
}

export const getDriverTruck = (record: any, truckId: Identifier) => {
  if (!truckId) {
    return null
  }
  const trucks = get(record, 'team.trucks')
  return trucks.find((truck: any) => truck.id === truckId)
}

export const getJobInfo = (record: any, user: any) => {
  const load = record?.load
  const broker = get(load, 'broker')
  const mobile = get(user, 'mobile')
  const email = get(user, 'email')
  const active_eld_account = get(user, 'driver.active_eld_account')
  const truck = get(user, 'driver.assigned_truck')
  const truckNumber = get(truck, 'vehicle_number')
  const team = get(record, 'team')
  // Since there is no explicit way to associate a job with a trailer number, try to get the trailer number the following ways:
  // 1) Check if the load stops have a trailer number (this is typical for PO loads).
  // 2) If the team has only one trailer, use that trailer's number.
  let trailerNumber = load?.stops
    ?.filter((stop: any) => stop.trailer_number !== undefined && stop.trailer_number !== '')
    .map((stop: any) => stop.trailer_number)
    .filter((value: any, index: any, self: any) => self.indexOf(value) === index)
    .join(', ')
  if (trailerNumber === undefined || trailerNumber === '') {
    if (team?.trailers?.length === 1) {
      trailerNumber = team?.trailers[0].vehicle_number
    }
  }
  const productSubscription = get(record, 'product_subscription')

  let jobInfo = `Brokerage Load Number: ${getStringValueOrEmpty(load?.source_human_id)}
Assigned Driver: ${getStringValueOrEmpty(getUserFullName(user) ?? '')}
Mobile: ${getStringValueOrEmpty(mobile)}
Email: ${getStringValueOrEmpty(email)}
Vehicle Id: ${getStringValueOrEmpty(active_eld_account?.provider_id)} ${getStringValueOrEmpty(
    active_eld_account?.provider_type
  )}
Truck Number: ${getStringValueOrEmpty(truckNumber)}
Trailer Number: ${getStringValueOrEmpty(trailerNumber)}`

  if (
    productSubscription &&
    [ProductSubscriptionBundleName.CASH, ProductSubscriptionBundleName.FLEX].includes(
      productSubscription.bundle_name
    )
  ) {
    const stops = load?.stops
    const firstStop = stops?.find((stop: any) => stop.sequence_number === 1)
    const firstStopLocation = `${firstStop?.location_city}, ${firstStop?.location_state}`
    const lastStop = stops?.find((stop: any) => stop.sequence_number === stops.length)
    const lastStopLocation = `${lastStop?.location_city}, ${lastStop?.location_state}`
    jobInfo += `\nCarrier: ${getStringValueOrEmpty(team?.name)}
Carrier MC #: ${getStringValueOrEmpty(team?.mc_number) || getStringValueOrEmpty(team?.dot_number)}
Lane: ${firstStopLocation} to ${lastStopLocation}
Broker: ${broker?.name}`
  }

  return jobInfo
}

const getStringValueOrEmpty = (value?: string) => {
  return value ?? ''
}

// given load stop, strip offsets and convert dates to ISO strings
export const stripStopOffsets = (stop: any) => {
  const start = get(stop, 'date_start')
  const end = get(stop, 'date_end')
  if (DateTime.isDateTime(start)) {
    set(stop, 'date_start', start.toISO({ includeOffset: false, suppressMilliseconds: true }))
  }
  if (DateTime.isDateTime(end)) {
    set(stop, 'date_end', end.toISO({ includeOffset: false, suppressMilliseconds: true }))
  }
}

// This function is used to 'clean' DateTimes in forms before sending a request.
// We want to send naive datetimes to our backend, & this strips load stop timezones from appt times.
export const stripOffsetsFromStopDateTimes = (values: any, isJob = true) => {
  if (!values) return null
  let stops = isJob ? values.load.stops : values.stops
  each(stops, (stop) => stripStopOffsets(stop))
  return values
}

// This function is used in Job forms to localize load stop datetimes to the stop timezone, and then
// make the datetime value naive.
export const localizeStopDateTimesForIntitalValues = (job: any) => {
  if (!job) {
    return null
  }
  var stops = job.load.stops
  for (let i = 0; i < stops.length; i++) {
    var stop = stops[i]
    stop.date_start = localizeDateTimeAndStripOffset(stop.date_start, stop.location_timezone)
    stop.date_end = localizeDateTimeAndStripOffset(stop.date_end, stop.location_timezone)
  }
  return job
}

export const localizeDateTimeAndStripOffset = (date: string, timezone: string) => {
  return DateTime.fromISO(date, { zone: timezone }).toISO({ includeOffset: false })
}

export function getEquipmentResourceName(equipmentType: string) {
  return equipmentType === EquipmentType.Truck ? 'trucks' : 'trailers'
}

// Helper to replace any regex escape chars in a string before matching against it
// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
export const safeMatch = (string: string, matchString: string) => {
  const regexString = matchString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
  return string.match(regexString)
}

export function getVGSUrl() {
  const env = detectEnvironment()
  const consoleOrigin = detectConsoleOrigin()
  switch (env) {
    case Environment.STAGING:
    case Environment.PREVIEW:
      if (consoleOrigin === 'APP') {
        return 'https://vgs.app.cloudtrucks-staging.com'
      }
      return 'https://vgs.console.cloudtrucks-staging.com'

    case Environment.PRODUCTION:
      if (consoleOrigin === 'APP') {
        return 'https://vgs.app.cloudtrucks.com'
      }
      return 'https://vgs.console.cloudtrucks.com'

    case Environment.DEVELOPMENT:
    default:
      return `/__vgs`
  }
}

function detectConsoleOrigin() {
  const originUrl = window.location.origin
  switch (originUrl) {
    case 'https://console.cloudtrucks.com':
    case 'https://console.cloudtrucks-staging.com':
      return 'CONSOLE'
    case 'https://app.cloudtrucks.com':
    case 'https://app.cloudtrucks-staging.com':
    default:
      return 'APP'
  }
}

// https://stackoverflow.com/questions/36280818/how-to-convert-file-to-base64-in-javascript/57272491#57272491
// https://stackoverflow.com/questions/6965107/converting-between-strings-and-arraybuffers

export function convertFileToBase64(file: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      if (!reader.result) {
        return reject()
      }

      if (reader.result instanceof ArrayBuffer) {
        const enc = new TextDecoder('utf-8')
        return resolve(enc.decode(reader.result))
      }

      return resolve(reader.result)
    }
    reader.onerror = (error) => reject(error)
  })
}

export function base64toBlob(base64Data: string) {
  // https://stackoverflow.com/a/16245768
  const splitBase64 = base64Data.split(';')
  const encodedFile = splitBase64[1].split(',')[1]
  var contentType = splitBase64[0].split(':')[1] || ''
  var sliceSize = 1024

  const byteCharacters = atob(encodedFile)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })
  return blob
}

export const sanitizeNonNullableStringFields = (values: any, nonNullableStringFields: any) => {
  for (let strValue of nonNullableStringFields) {
    if (get(values, strValue) === null) {
      set(values, strValue, '')
    }
  }
  return values
}

export const sanitizeNonNullableFieldsOnLoad = (values: any, isJob: boolean) => {
  const nonNullableLoadFields = ['comment', 'company_name', 'contact_name', 'contact_phone']
  const nonNullableLoadStopFields = [
    'location_name',
    'location_address1',
    'location_address2',
    'location_zip',
    'notes',
  ]

  let load = isJob ? get(values, 'load') : values
  sanitizeNonNullableStringFields(load, nonNullableLoadFields)

  for (let stop of get(load, 'stops')) {
    sanitizeNonNullableStringFields(stop, nonNullableLoadStopFields)
  }
  return values
}

export function getReadableEldTypeName(eldType: any) {
  return get(ELDTypeName, eldType, eldType)
}

export function getBrokerReferenceFilter(is_premium: boolean) {
  const brokerInputFieldFilters: Record<string, any> = {
    status__not: BrokerStatus.Invalid.id,
  }
  if (is_premium) {
    brokerInputFieldFilters['is_carrier_packet_complete'] = true
    brokerInputFieldFilters['status'] = BrokerStatus.Approved.id
  }
  return brokerInputFieldFilters
}

export function getPaymentPolicyLinkForId(policy_id: Identifier) {
  let ctOrigin = window.location.origin
  const env = detectEnvironment()
  if (env === Environment.DEVELOPMENT) {
    ctOrigin = 'http://localhost:8000'
  }
  return `${ctOrigin}/ct-admin/payments/periodicpaymentpolicy/${policy_id}/change/`
}

/**
 * Invokes [callback] on a schedule of every [delay] milliseconds.
 * Note that there will only ever be max 1 invocation of [callback].
 * For more details see: https://stackoverflow.com/a/61407381
 * @param {() => Promise<T> | (() => void)} callback
 * @param {number | null} delay
 * @return {void}
 */
export function useRecursiveTimeout(callback: () => Promise<any> | (() => void), delay: number | null) {
  const savedCallback = useRef(callback)

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the timeout loop.
  useEffect(() => {
    let timer: NodeJS.Timeout

    function tick() {
      const ret = savedCallback.current()

      if (ret instanceof Promise) {
        ret.then(() => {
          if (delay !== null) {
            timer = setTimeout(tick, delay)
          }
        })
      } else {
        if (delay !== null) {
          timer = setTimeout(tick, delay)
        }
      }
    }

    if (delay !== null) {
      timer = setTimeout(tick, delay)
      return () => timer && clearTimeout(timer)
    }
  }, [delay])
}

export function captureExceptionIfProduction(error: any) {
  if (isProductionEnv() && error?.response?.status !== 400) {
    Sentry.captureMessage(
      `Error: ${error?.response?.statusText} ${error?.response?.status}: ${JSON.stringify(
        error?.response?.data
      )}`,
      'error' as SeverityLevel
    )
  }
}

export function getReadableErrorMessage(error: any) {
  if (error?.response) {
    return JSON.stringify(error.response.data)
  }
  return 'No error message found.'
}

export function getProductNameFromRecord(record: any, isShortName: boolean = false) {
  const { team, product_subscription } = record
  let bundleName
  if (product_subscription) {
    bundleName = product_subscription.bundle_name
  } else if (team) {
    bundleName = team.product_subscription?.bundle_name || team.product_subscription_name
  }
  if (bundleName) {
    if (bundleName === ProductSubscriptionBundleName.PREMIUM) {
      const subscription = product_subscription || team.product_subscription
      if (subscription.carrier) {
        return `${subscription.carrier.name} - MC ${subscription.carrier.mc_number}`
      }
    }
    return getProductName(bundleName, isShortName)
  }
  return 'No product subscription'
}

export function getBundleById(bundle_id: string) {
  const target_bundle = BundleChoices.filter((choice) => choice.id === bundle_id)[0]
  return target_bundle
}

export function getOutstandingCashAdvancesOnJob(job: any) {
  const statements = job?.statements
  return statements?.flatMap((statement: any) =>
    statement.cash_advance_payments.filter(
      (payment: any) => payment.status !== CashAdvancePaymentStatuses.CANCELED
    )
  )
}
